import { useEffect } from "react";

/**
 * Restricts an Alchemy `<Modal>` to closing via its X button, suppressing the Escape key and
 * backdrop-click dismissals.
 *
 * This can't be done from the modal's `onClose` handler. All four of Alchemy's dismissal paths — the X,
 * the footer close button, Escape, and a backdrop click — funnel through the same `open` setter, which
 * dispatches one indistinguishable `close` CustomEvent and then tears the modal down. That event is not
 * cancelable (nothing in the bundle sets `cancelable`), so by the time `onClose` runs the decision is
 * already made and `preventDefault` does nothing. The only place left to intervene is ahead of the
 * component's own listeners.
 *
 * Both listeners are therefore registered in the CAPTURE phase and use stopImmediatePropagation:
 *
 * - Escape: the component listens on `document` in the bubble phase (added in its `init`), so a capture
 *   listener on `document` runs first. Only Escape is swallowed — the same handler implements the Tab
 *   focus trap, which must keep working.
 * - Backdrop: the component listens on its shadow-root container, a descendant of the host, so a
 *   capture listener on the host runs first. A click is allowed through when its composedPath includes
 *   the shadow `.content` element — the same test the component itself applies — which covers the
 *   header (and so the X), the body and the footer, and leaves only the backdrop suppressed.
 *
 * Reaching for `.content` is a dependency on Alchemy's internals. It's the narrowest one available:
 * the alternative is hard-coding the X button's `#closeIcon` id, which is no more stable and would
 * have to enumerate every other in-content region instead. If Alchemy renames it, the selector misses,
 * `content` is null, and this falls back to suppressing nothing — the pre-existing behaviour — rather
 * than trapping the operator in a modal they can't close.
 *
 * @param active whether the restriction should apply at all. Callers pass false for states where
 *               nothing would be lost by a dismissal, so Escape keeps working there.
 * @param modalId the `<Modal>`'s id, used to find the host element.
 */
export const useCloseButtonOnlyDismissal = (active: boolean, modalId: string): void => {
    useEffect(() => {
        if (!active) {
            return;
        }
        // Alchemy re-parents the host to document.body while open, so look it up by id rather than
        // holding a ref to where it was rendered.
        const host = document.getElementById(modalId);
        if (!host) {
            return;
        }
        const content = host.shadowRoot?.querySelector('.content') ?? null;

        const onClickCapture = (event: Event) => {
            if (content && event.composedPath().includes(content)) {
                return; // inside the modal proper — header (X), body or footer
            }
            if (!content) {
                return; // selector missed: leave Alchemy's behaviour alone rather than trapping anyone
            }
            event.stopImmediatePropagation();
        };

        const onKeyDownCapture = (event: KeyboardEvent) => {
            // Match on both: the component reads which/keyCode, and tests dispatch `key`.
            if (event.key === 'Escape' || event.keyCode === 27) {
                event.stopImmediatePropagation();
            }
        };

        host.addEventListener('click', onClickCapture, true);
        document.addEventListener('keydown', onKeyDownCapture, true);
        return () => {
            host.removeEventListener('click', onClickCapture, true);
            document.removeEventListener('keydown', onKeyDownCapture, true);
        };
    }, [active, modalId]);
};
